import { Box, Separator } from '@radix-ui/themes'
import React, { useState } from 'react'

import { UserProfileRole } from '../modules/users/types/UserProfile'

import DocNotes from './DocNotes'
import { DocHeader, DocHeaderType } from './DocHeader'
import { OrganizationFrame, OrganizationFrameType } from './OrganizationFrame'
import { ClientFrame, ClientFrameType } from './ClientFrame'
import { VehicleFrame, VehicleFrameType } from './VehicleFrame'
import { SettlementShort } from './SettlementEdit'
import DocFooter from './DocFooter'
import { ButtonCheckConfirma, ButtonPrint } from './Buttons'
import { DocStatusHeader } from './DocStatusHeader'
import { PrintableCard } from './PrintableCard'

import { formatCurrency, formatDate } from '@/utils/utils'
import { ExpenseFormat } from '@/interfaces/VehicleBase'
import Settlement from '@/interfaces/Settlement'
import ClientBase from '@/interfaces/ClientBase'
import VehicleBase from '@/interfaces/VehicleBase'
import { useAuth } from '@/hooks/useAuth'
import DocToHeader from '@/interfaces/DocInactive'
import { DocStateSpecial } from '@/enums/DocStates'

interface Props {
  vehicle: VehicleBase
  client: ClientBase
  settlement: SettlementShort | Settlement
  costs: ExpenseFormat[]
  consultaSw: boolean
  handleAnularClick?: (motivo: string) => void
}

export const SettlementPreview = ({ vehicle, client, settlement, costs, consultaSw, handleAnularClick }: Props) => {
  const [motivo, setMotivo] = useState<string>('')
  const { userData, hasRole } = useAuth()
  const myNumberRes = (settlement?.number === 0 ? '' : settlement?.number) ?? ''
  const isAnulada: boolean = !!settlement?.voidedAt
  const docHeader: DocHeaderType = {
    urlImage: settlement.body.organization.pictures.logotype,
    titulo: `Liquidación de Venta Nro. ${myNumberRes}`,
    isAnulada: isAnulada,
    tituloAnulada: 'NULA',
    fechaIniText: `Fecha`,
    fechaIniValue: formatDate(settlement?.date),
    fechaFinText: '',
    fechaFinValue: '',
  }

  const client_name = `${client.firstname} ${client.lastname}`

  let profileName = ''

  if (!settlement.body.profile) {
    profileName = userData?.user?.fullname ?? ''
  } else {
    profileName = `${settlement.body.profile?.user.firstname} ${settlement.body.profile?.user.lastname}`
  }

  const user = { name: profileName, phone: settlement.body.profile?.user.phone ?? '' }
  const organizationFrame: OrganizationFrameType = {
    ...settlement.body.organization,
    user: user,
  }
  const clientFrame: ClientFrameType = {
    ...client,
    name: client_name,
  }

  const vehicleFrame: VehicleFrameType = {
    ...vehicle,
  }

  const organizationName = settlement.body.organization.name

  let docInactive: DocToHeader = {
    state: DocStateSpecial.Activo,
    date: '',
    cause: '',
    by: '',
  }

  if (isAnulada) {
    docInactive = {
      state: DocStateSpecial.Nulo,
      date: settlement?.voidedAt ?? '',
      cause: settlement.voidedCause ?? '',
      by: settlement?.voidedBy_user ?? '',
    }
  }
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target

    if (name === 'motivo') {
      setMotivo(value)
    }
  }

  const canVoid = hasRole(UserProfileRole.VoidSettlements)

  return (
    <>
      <PrintableCard>
        <DocStatusHeader docName="liquidación" state={docInactive} />
        <DocHeader header={docHeader} />

        <Separator size="4" my="4" />

        <OrganizationFrame organization={organizationFrame} />

        <Separator size="4" my="4" />

        <ClientFrame client={clientFrame} />

        <Separator size="4" my="4" />

        <VehicleFrame vehicle={vehicleFrame} noPrice={true} />

        <Separator size="4" my="4" />

        <DocNotes notes={settlement?.notes} />

        <div className="border-2 border-black pl-2 mt-2">
          <div className="grid grid-cols-2">
            <div className="pr-4">{'Liquidación a favor del cliente'}</div>
            <div className="pr-4 text-right">{formatCurrency(settlement.price)}</div>
          </div>
          <GridDet paycosts={costs} />
          <div className="grid grid-cols-2">
            <div className="pr-4 font-bold">{'Total liquidación'}</div>
            <div className="pr-4 text-right font-bold">{formatCurrency(settlement.total)}</div>
          </div>
        </div>
        <div className="border-2 border-black pl-2 mt-2">
          <div className="grid grid-cols-2">
            <div className="pr-4">{'Medio de pago'}</div>
            <div className="pr-4">{settlement.payment}</div>
          </div>
        </div>
        <div>
          Acepto la suma indicada <span className="font-bold">({settlement.totalInWords})</span>
          {` en ${settlement.payment}`} a mi entera satisfacción y sin ningún tipo de reparo posterior.
        </div>

        <DocFooter
          names={{
            profileName: profileName,
            organizationName: organizationName,
            clientName: client_name,
          }}
        />
      </PrintableCard>

      <Box className="print:hidden" mt="6" mb="2">
        {consultaSw && <ButtonPrint />}
      </Box>

      <Box className="print:hidden">
        {!isAnulada && consultaSw && handleAnularClick && canVoid && (
          <ButtonCheckConfirma
            title="Anular Compra"
            subTitle="Al anular la liquidación, esta ya no será legalmente válida."
            checkLabel="Estoy seguro de anular esta liquidación."
            buttonLabel="Anular"
            handleCrearClick={() => handleAnularClick(motivo)}
            handleTextAreaChange={handleInputChange}
            textAreaLabel="Motivo de anulación"
            textAreaName="motivo"
            textAreaValue={motivo}
          />
        )}
      </Box>
    </>
  )
}

const GridDet: React.FC<{ paycosts: ExpenseFormat[] }> = ({ paycosts }) => {
  return (
    <div className="grid grid-cols-2">
      {paycosts.map((c) => (
        <>
          {!c.excluded && (
            <React.Fragment key={c._id}>
              <div className="pr-4">{c.description}</div>

              <div className="pr-4 text-right">-${c.cost_format}</div>
            </React.Fragment>
          )}
        </>
      ))}
    </div>
  )
}

export default SettlementPreview
